// pages/AwsCostBriefPage.jsx — Monthly AWS cost brief settings.
// ─────────────────────────────────────────────────────────────────────────────
// Flow: calibrate thresholds against past months → inspect the exact message →
// turn it on. Backend: /api/aws-cost/brief/{config,preview,run}.
// Follows house style: axios from AuthContext (token injected), Tailwind.
//
// Design note: the centre of this page is NOT the threshold form, it's the
// calibration table. A threshold number on its own can't be judged right or
// wrong — what can be judged is "which months would have alerted me". So the
// form and the calibration result must be visible together, and changing a
// threshold recomputes the verdict column client-side without another request.
//
// i18n: strings live in a local dictionary rather than context/translations.js.
// That file is ~570KB and shared by every page; adding keys there for one page
// means editing three language blocks with no way to verify the live copy. The
// language switcher still works — see `L` below. If you'd rather keep these in
// translations.js (and want Arabic too), STR lifts out cleanly.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from 'react';
import { api as axiosApi } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import {
  Wallet, Play, Send, Save, Loader2, AlertTriangle,
  CheckCircle2, BellRing, BellOff, Info,
} from 'lucide-react';

const STR = {
  en: {
    title: 'AWS cost brief',
    subtitle: 'A monthly Telegram summary when spend moves outside its usual range',
    onTitle: 'Brief is on', offTitle: 'Brief is off',
    schedule: 'Runs on the 3rd of each month, comparing the last two complete months.',
    turnOn: 'Turn on', turnOff: 'Turn off',
    cronWarn1: 'The schedule is a separate switch. Open ', cronWarn2: 'Cron Manager',
    cronWarn3: ' and enable the ', cronWarn4: " job if you haven't. Without it the brief never sends.",
    thresholds: 'Thresholds',
    thresholdsHint: 'All in USD, measured against real change — already corrected for the difference in day count between months.',
    fMain: 'Main threshold', fMainHint: 'The brief includes a breakdown once total real change reaches this',
    fBu: 'Per business unit', fBuHint: 'A BU is named in the brief when it moves by this much',
    fLine: 'Per service', fLineHint: 'A service or usage type is named when it moves by this much',
    fNew: 'New item', fNewHint: 'A service that was previously zero is reported once it reaches this',
    narrate: 'Add a plain-language summary',
    narrateHint: "AI turns the already-computed figures into a few sentences. The numbers don't change, and if the AI call fails the table still sends.",
    save: 'Save', discard: 'Discard changes', saved: 'Settings saved.',
    calTitle: 'Test against past months',
    calHint: 'Computes the last 6 complete months without sending anything. Use it to see which months would trigger a brief at your current thresholds.',
    compute: 'Compute', computing: 'Computing…',
    empty: 'Press Compute to see the last 6 months.',
    thMonth: 'Month', thTotal: 'Total', thReal: 'Real change', thVerdict: 'At these thresholds',
    vFire: 'sends breakdown', vNormal: 'reports normal', vUnknown: 'unknown',
    vNoData: 'no data', vStale: 'needs backend update',
    view: 'View message', close: 'Close', send: 'Send',
    sendTip: "Send this month's brief to Telegram now",
    exact: 'This is exactly what gets sent, except the AI summary, which is written fresh at send time.',
    sumA: 'At a', sumB: 'threshold,', sumC: 'of', sumD: 'months would send a breakdown.',
    allFire: 'Every month triggers — this threshold is too low for how much your spend moves. Raise it.',
    noneFire: 'Nothing triggers. If you remember a month that spiked, lower it.',
    someFire: 'That spread looks reasonable — the brief will speak up only when something moves.',
    staleWarn: "The backend didn't return figures, so the verdict column can't be computed here. Deploy the current awsCostBrief.js to enable live threshold testing.",
    footer: "The brief sends every month, including quiet ones. That's deliberate: a brief that doesn't arrive should mean something is broken, not that everything is fine.",
    loading: 'Loading brief settings…',
    errLoad: 'Could not load settings.', errSave: 'Could not save.', errSend: 'Could not send.',
    sentOk: 'Brief sent.',
  },
  id: {
    title: 'Brief biaya AWS',
    subtitle: 'Ringkasan bulanan ke Telegram saat biaya berubah di luar kebiasaan',
    onTitle: 'Brief aktif', offTitle: 'Brief belum aktif',
    schedule: 'Tanggal 3 tiap bulan, membandingkan dua bulan lengkap terakhir.',
    turnOn: 'Aktifkan', turnOff: 'Matikan',
    cronWarn1: 'Jadwalnya switch terpisah. Buka ', cronWarn2: 'Cron Manager',
    cronWarn3: ' dan aktifkan job ', cronWarn4: ' kalau belum. Tanpa itu brief tidak pernah terkirim.',
    thresholds: 'Ambang',
    thresholdsHint: 'Semua dalam USD, dihitung dari perubahan riil — sudah dikoreksi terhadap perbedaan jumlah hari antar bulan.',
    fMain: 'Ambang utama', fMainHint: 'Brief menyertakan rincian bila perubahan riil total mencapai angka ini',
    fBu: 'Per business unit', fBuHint: 'BU disebut di brief bila berubah sebesar ini',
    fLine: 'Per layanan', fLineHint: 'Service atau usage type disebut bila berubah sebesar ini',
    fNew: 'Item baru', fNewHint: 'Layanan yang sebelumnya nol dilaporkan bila mencapai ini',
    narrate: 'Tambahkan ringkasan bahasa manusia',
    narrateHint: 'AI merangkai angka yang sudah dihitung jadi beberapa kalimat. Angkanya tidak berubah, dan bila AI gagal tabelnya tetap terkirim.',
    save: 'Simpan', discard: 'Batalkan perubahan', saved: 'Pengaturan disimpan.',
    calTitle: 'Uji dengan data lampau',
    calHint: 'Menghitung 6 bulan lengkap terakhir tanpa mengirim apa pun. Pakai ini untuk melihat bulan mana saja yang akan memicu brief dengan ambang sekarang.',
    compute: 'Hitung', computing: 'Menghitung…',
    empty: 'Tekan Hitung untuk melihat 6 bulan terakhir.',
    thMonth: 'Bulan', thTotal: 'Total', thReal: 'Perubahan riil', thVerdict: 'Dengan ambang ini',
    vFire: 'kirim rincian', vNormal: 'lapor normal', vUnknown: 'tidak diketahui',
    vNoData: 'data tidak ada', vStale: 'backend perlu diperbarui',
    view: 'Lihat pesan', close: 'Tutup', send: 'Kirim',
    sendTip: 'Kirim brief bulan ini ke Telegram sekarang',
    exact: 'Ini persis yang akan dikirim, kecuali ringkasan AI yang baru dibuat saat pengiriman.',
    sumA: 'Dengan ambang', sumB: '—', sumC: 'dari', sumD: 'bulan akan mengirim rincian.',
    allFire: 'Semua bulan memicu — ambang ini terlalu rendah untuk sebaran biayamu. Naikkan.',
    noneFire: 'Tidak ada yang memicu. Kalau kamu ingat ada bulan yang membengkak, turunkan.',
    someFire: 'Sebaran ini masuk akal — brief bicara hanya saat ada yang berubah.',
    staleWarn: 'Backend tidak mengirim angka, jadi kolom putusan tidak bisa dihitung di sini. Deploy awsCostBrief.js versi terbaru untuk mengaktifkan uji ambang langsung.',
    footer: 'Brief terkirim tiap bulan, termasuk bulan tenang. Ini disengaja: brief yang tidak datang harus berarti ada yang rusak, bukan berarti semuanya aman.',
    loading: 'Memuat pengaturan brief…',
    errLoad: 'Gagal memuat pengaturan.', errSave: 'Gagal menyimpan.', errSend: 'Gagal mengirim.',
    sentOk: 'Brief terkirim.',
  },
};

const MONTHS = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  id: ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'],
};

const usd = (n) => '$' + Math.round(Number(n) || 0).toLocaleString('en-US');
const signed = (n) => (Number(n) < 0 ? '−' : '+') + usd(Math.abs(Number(n) || 0));

// Last n complete months. Deliberately never includes the running month:
// a month-over-month diff on a partial month always looks like a steep drop.
function closedMonths(n = 6) {
  const now = new Date();
  const out = [];
  for (let i = 1; i <= n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
  }
  return out;
}

export default function AwsCostBriefPage() {
  const { lang } = useLang();
  const L = STR[lang] || STR.en;                 // ar and anything else → English
  const M = MONTHS[lang] || MONTHS.en;

  const [cfg, setCfg]         = useState(null);
  const [draft, setDraft]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState(null);
  const [rows, setRows]       = useState([]);
  const [scanning, setScan]   = useState(false);
  const [openText, setOpen]   = useState(null);
  const [sending, setSending] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axiosApi.get('/api/aws-cost/brief/config');
      setCfg(data); setDraft(data);
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.error || L.errLoad });
    } finally { setLoading(false); }
  }, [L.errLoad]);

  useEffect(() => { load(); }, [load]);

  const scan = async () => {
    setScan(true); setRows([]); setMsg(null);
    const out = [];
    for (const p of closedMonths(6)) {
      try {
        const { data } = await axiosApi.post('/api/aws-cost/brief/preview', p);
        out.push({ ...p, ...data });
      } catch (e) {
        out.push({ ...p, error: e.response?.data?.error || 'failed' });
      }
      setRows([...out]);            // render progressively, don't wait for all 6
    }
    setScan(false);
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const { data } = await axiosApi.put('/api/aws-cost/brief/config', draft);
      setCfg(data.config); setDraft(data.config);
      setMsg({ kind: 'ok', text: L.saved });
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.error || L.errSave });
    } finally { setSaving(false); }
  };

  const sendNow = async (p) => {
    setSending(`${p.year}-${p.month}`); setMsg(null);
    try {
      const { data } = await axiosApi.post('/api/aws-cost/brief/run', { year: p.year, month: p.month });
      setMsg({ kind: 'ok', text: data.message || L.sentOk });
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.error || L.errSend });
    } finally { setSending(null); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-[calc(100vh-4rem)] gap-2 text-slate-400">
      <Loader2 className="animate-spin" size={16} />
      <span className="text-xs">{L.loading}</span>
    </div>
  );

  const dirty = cfg && draft && JSON.stringify(cfg) !== JSON.stringify(draft);
  const gate = Number(draft?.total_min_abs_usd) || 0;

  // Verdict resolution, in order of trust:
  //   1. `stats` present → recompute locally against the DRAFT threshold, so
  //      editing the number updates the column with no extra request.
  //   2. no stats but server sent `overGate` → show the server's verdict, and
  //      flag that live recomputation isn't available.
  //   3. neither → 'unknown'. Never fall through to "reports normal": claiming
  //      a month is quiet when we simply don't know is worse than admitting it.
  const decided = rows.map(r => ({
    ...r,
    would: r.stats
      ? Math.abs(Number(r.stats.real_change)) >= gate
      : (typeof r.overGate === 'boolean' ? r.overGate : null),
    live: !!r.stats,
  }));
  const usable = decided.filter(r => r.would !== null && !r.error && !r.noData);
  const nFired = usable.filter(r => r.would).length;
  const stale  = decided.some(r => !r.live && !r.error && !r.noData);

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div style={{ background: "linear-gradient(135deg, rgb(var(--brand-600)) 0%, rgb(var(--brand-400)) 100%)", borderRadius: 14, padding: '16px 22px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.4px' }}>{L.title}</h1>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', margin: '3px 0 0' }}>{L.subtitle}</p>
      </div>

      {msg && (
        <div className={`flex items-start gap-2 rounded-xl px-4 py-3 text-sm ${
          msg.kind === 'ok' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
          {msg.kind === 'ok' ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                             : <AlertTriangle size={16} className="mt-0.5 shrink-0" />}
          <span>{msg.text}</span>
        </div>
      )}

      {/* On / off */}
      <div className="rounded-2xl border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            {Number(draft.enabled)
              ? <BellRing className="text-emerald-600 mt-0.5 shrink-0" size={18} />
              : <BellOff className="text-slate-400 mt-0.5 shrink-0" size={18} />}
            <div>
              <p className="text-sm font-medium text-slate-800">
                {Number(draft.enabled) ? L.onTitle : L.offTitle}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">{L.schedule}</p>
            </div>
          </div>
          <button
            onClick={() => setDraft({ ...draft, enabled: Number(draft.enabled) ? 0 : 1 })}
            className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
              Number(draft.enabled)
                ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}>
            {Number(draft.enabled) ? L.turnOff : L.turnOn}
          </button>
        </div>

        {Number(draft.enabled) && (
          <div className="mt-4 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
            <Info size={14} className="mt-0.5 shrink-0" />
            <span>
              {L.cronWarn1}<span className="font-medium">{L.cronWarn2}</span>{L.cronWarn3}
              <code className="font-mono bg-amber-100 px-1 rounded">aws_cost_brief</code>
              {L.cronWarn4}
            </span>
          </div>
        )}
      </div>

      {/* Thresholds */}
      <div className="rounded-2xl border border-slate-200 p-5 space-y-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">{L.thresholds}</h2>
          <p className="text-xs text-slate-500 mt-0.5">{L.thresholdsHint}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={L.fMain} hint={L.fMainHint} value={draft.total_min_abs_usd}
            onChange={(v) => setDraft({ ...draft, total_min_abs_usd: v })} />
          <Field label={L.fBu} hint={L.fBuHint} value={draft.bu_min_abs_usd}
            onChange={(v) => setDraft({ ...draft, bu_min_abs_usd: v })} />
          <Field label={L.fLine} hint={L.fLineHint} value={draft.line_min_abs_usd}
            onChange={(v) => setDraft({ ...draft, line_min_abs_usd: v })} />
          <Field label={L.fNew} hint={L.fNewHint} value={draft.new_item_min_usd}
            onChange={(v) => setDraft({ ...draft, new_item_min_usd: v })} />
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer pt-1">
          <input type="checkbox" checked={!!Number(draft.narrate)}
            onChange={(e) => setDraft({ ...draft, narrate: e.target.checked ? 1 : 0 })}
            className="mt-0.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
          <span className="text-sm text-slate-700">
            {L.narrate}
            <span className="block text-xs text-slate-500 mt-0.5">{L.narrateHint}</span>
          </span>
        </label>

        <div className="flex items-center gap-3 pt-1">
          <button onClick={save} disabled={!dirty || saving}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium
                       hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed
                       flex items-center gap-2 transition">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            {L.save}
          </button>
          {dirty && (
            <button onClick={() => setDraft(cfg)} className="text-sm text-slate-500 hover:text-slate-700">
              {L.discard}
            </button>
          )}
        </div>
      </div>

      {/* Calibration */}
      <div className="rounded-2xl border border-slate-200 p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">{L.calTitle}</h2>
            <p className="text-xs text-slate-500 mt-0.5 max-w-lg">{L.calHint}</p>
          </div>
          <button onClick={scan} disabled={scanning}
            className="shrink-0 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs
                       font-medium hover:bg-slate-200 disabled:opacity-50
                       flex items-center gap-1.5 transition">
            {scanning ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
            {scanning ? L.computing : L.compute}
          </button>
        </div>

        {!decided.length && !scanning && (
          <p className="text-sm text-slate-400 py-6 text-center">{L.empty}</p>
        )}

        {stale && (
          <div className="flex items-start gap-2 rounded-xl bg-amber-50 px-3.5 py-3 text-xs text-amber-800">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{L.staleWarn}</span>
          </div>
        )}

        {!!decided.length && (
          <>
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-slate-400 text-left">
                    <th className="font-medium px-1 pb-2">{L.thMonth}</th>
                    <th className="font-medium px-1 pb-2 text-right">{L.thTotal}</th>
                    <th className="font-medium px-1 pb-2 text-right">{L.thReal}</th>
                    <th className="font-medium px-1 pb-2">{L.thVerdict}</th>
                    <th className="px-1 pb-2" />
                  </tr>
                </thead>
                <tbody>
                  {decided.map((r) => {
                    const key = `${r.year}-${r.month}`;
                    return (
                      <React.Fragment key={key}>
                        <tr className="border-t border-slate-100">
                          <td className="px-1 py-2.5 text-slate-700 whitespace-nowrap">
                            {M[r.month - 1]} {r.year}
                          </td>
                          <td className="px-1 py-2.5 text-right text-slate-600 tabular-nums">
                            {r.stats ? usd(r.stats.total_usd) : '—'}
                          </td>
                          <td className={`px-1 py-2.5 text-right tabular-nums font-medium ${
                            !r.stats ? 'text-slate-400'
                              : Number(r.stats.real_change) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                            {r.stats ? signed(r.stats.real_change) : '—'}
                          </td>
                          <td className="px-1 py-2.5">
                            {r.error ? (
                              <span className="text-xs text-red-600">{r.error}</span>
                            ) : r.noData ? (
                              <span className="text-xs text-amber-700">{L.vNoData}</span>
                            ) : r.would === null ? (
                              <span className="text-xs text-slate-400">{L.vUnknown}</span>
                            ) : r.would ? (
                              <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
                                {L.vFire}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-500">{L.vNormal}</span>
                            )}
                            {!r.live && !r.error && !r.noData && r.would !== null && (
                              <span className="block text-[11px] text-amber-600 mt-0.5">{L.vStale}</span>
                            )}
                          </td>
                          <td className="px-1 py-2.5 text-right whitespace-nowrap">
                            {r.text && (
                              <button onClick={() => setOpen(openText === key ? null : key)}
                                className="text-xs text-slate-500 hover:text-slate-800 mr-2">
                                {openText === key ? L.close : L.view}
                              </button>
                            )}
                            {r.text && (
                              <button onClick={() => sendNow(r)} disabled={sending === key} title={L.sendTip}
                                className="text-xs text-emerald-700 hover:text-emerald-900
                                           disabled:opacity-50 inline-flex items-center gap-1">
                                {sending === key ? <Loader2 size={11} className="animate-spin" />
                                                 : <Send size={11} />}
                                {L.send}
                              </button>
                            )}
                          </td>
                        </tr>
                        {openText === key && (
                          <tr>
                            <td colSpan={5} className="px-1 pb-3">
                              <pre className="whitespace-pre-wrap font-mono text-xs bg-slate-50
                                              text-slate-700 rounded-xl p-3.5 leading-relaxed">
{r.text}
                              </pre>
                              <p className="text-xs text-slate-400 mt-1.5">{L.exact}</p>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {usable.length > 0 && (
              <div className="text-xs text-slate-600 bg-slate-50 rounded-xl px-3.5 py-3 leading-relaxed">
                {L.sumA} <span className="font-medium">{usd(gate)}</span> {L.sumB} {nFired} {L.sumC}{' '}
                {usable.length} {L.sumD}{' '}
                {nFired === usable.length ? L.allFire : nFired === 0 ? L.noneFire : L.someFire}
              </div>
            )}
          </>
        )}
      </div>

      <p className="text-xs text-slate-400 leading-relaxed">{L.footer}</p>
    </div>
  );
}

function Field({ label, hint, value, onChange }) {
  return (
    <div>
      <label className="block text-sm text-slate-700">{label}</label>
      <div className="mt-1.5 relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
        <input type="number" min="0" step="5" value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full pl-7 pr-3 py-2 rounded-lg border border-slate-300 text-sm
                     tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-500
                     focus:border-emerald-500" />
      </div>
      <p className="text-xs text-slate-400 mt-1 leading-snug">{hint}</p>
    </div>
  );
}
